import {HttpErrorResponse} from '@angular/common/http';
import {Inject, Injectable, Type} from '@angular/core';
import {TuiDialogService} from '@taiga-ui/core';
import {PolymorpheusComponent} from '@taiga-ui/polymorpheus';
import {firstValueFrom, Observable} from 'rxjs';
import {twoFactorAuthComponentToken} from './tokens/two-factor-auth-component.token';

export const twoFactorMaxAttempts = 3;

export const twoFactorMaxAttemptsError = '2FA max attempts exceeded';

export const twoFactorCancelledError = '2FA cancelled';

export function isTwoFactorCancelled(err: unknown): boolean {
  return err instanceof Error && err.message === twoFactorCancelledError;
}

export function isTwoFactorMaxAttempts(err: unknown): boolean {
  return err instanceof Error && err.message === twoFactorMaxAttemptsError;
}

export type TwoFactorVerifyFn<T> = (code: string) => Observable<T>;

export type TwoFactorDialogClose<T> = T | string | undefined | 'max_attempts';

export interface TwoFactorAuthDialogData<T = unknown> {
  path: string;
  payload: Object;
  verify?: TwoFactorVerifyFn<T>;
  maxAttempts?: number;
}

@Injectable({
  providedIn: 'root',
})
export class TwoFactorAuthService {
  constructor(
    private readonly dialogService: TuiDialogService,
    @Inject(twoFactorAuthComponentToken)
    private readonly twoFactorAuthComponent: Type<unknown>,
  ) {}

  async submit<T = string>(
    path: string,
    data: unknown,
    verify?: TwoFactorVerifyFn<T>,
  ): Promise<T> {
    const result = await firstValueFrom(
      this.dialogService.open<TwoFactorDialogClose<T>>(
        new PolymorpheusComponent(this.twoFactorAuthComponent),
        {
          data: {
            path,
            payload: data as Object,
            verify,
            maxAttempts: twoFactorMaxAttempts,
          } satisfies TwoFactorAuthDialogData<T>,
          dismissible: true,
          size: 'auto',
          appearance: 'no-padding',
        },
      ),
    );

    if (result === 'max_attempts') {
      throw new Error(twoFactorMaxAttemptsError);
    }
    if (result instanceof HttpErrorResponse || result instanceof Error) {
      throw result;
    }
    if (result === undefined || result === null || result === '') {
      throw new Error(twoFactorCancelledError);
    }

    return result as T;
  }
}
